import React, { useState, useEffect } from "react";
import $ from "jquery";
import "datatables.net-dt/js/dataTables.dataTables.js";
import { Row, Col, Card, Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import axios from 'axios';

const Balances = () => {
  const navigate = useNavigate();
  const BaseUrl = process.env.REACT_APP_API;
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limoPrice, setLimoPrice] = useState(null);
  const [limoVolume, setLimoVolume] = useState(null);
  const token = sessionStorage.getItem("authToken");
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [secondInputValue, setSecondInputValue] = useState(''); // For range inputs
  const [inputType, setInputType] = useState('single'); // 'single' or 'range'

  const [minUsdtBalance, setMinUsdtBalance] = useState(0);
  const [minLimoBalance, setMinLimoBalance] = useState(0);
  const [minIntervalSec, setMinIntervalSec] = useState(0);
  const [maxIntervalSec, setMaxIntervalSec] = useState(0);
  const [minOrderSize, setMinOrderSize] = useState(0);
  const [maxOrderSize, setMaxOrderSize] = useState(0);

  // Fetch all configuration data
  const fetchLimits = async () => {
    try {
      const response = await fetch(`${BaseUrl}/get_balances?bot_id=2`);
      const result = await response.json();
      const data = result.data;

      setMinUsdtBalance(parseFloat(data.min_usdt_balance));
      setMinLimoBalance(parseFloat(data.min_limo_balance));
      
      // Fetch interval settings
      const intervalResponse = await fetch(`${BaseUrl}/get_interval?bot_id=2`);
      const intervalData = await intervalResponse.json();
      setMinIntervalSec(parseInt(intervalData.data.min_interval_sec));
      setMaxIntervalSec(parseInt(intervalData.data.max_interval_sec));
      
      // Fetch order size settings
      const orderSizeResponse = await fetch(`${BaseUrl}/get_order_size?bot_id=2`);
      const orderSizeData = await orderSizeResponse.json();
      setMinOrderSize(parseInt(orderSizeData.data.min_order_size));
      setMaxOrderSize(parseInt(orderSizeData.data.max_order_size));
    } catch (error) {
      console.error("Failed to fetch configuration:", error.message);
    }
  };

  // Update trading interval
  const updateTradingInterval = async (min, max) => {
    try {
      const response = await fetch(`${BaseUrl}/update_interval`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bot_id: 2,
          min_interval_sec: min,
          max_interval_sec: max,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update interval");
      }

      setMinIntervalSec(min);
      setMaxIntervalSec(max);
      NotificationManager.success('Trading interval updated successfully!');
    } catch (error) {
      console.error("Error updating interval:", error.message);
      NotificationManager.error('Failed to update trading interval');
    }
  };

  // Update order size
  const updateOrderSize = async (min, max) => {
    try {
      const response = await fetch(`${BaseUrl}/update_order_size`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bot_id: 2,
          min_order_size: min,
          max_order_size: max,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update order size");
      }

      setMinOrderSize(min);
      setMaxOrderSize(max);
      NotificationManager.success('Order size updated successfully!');
    } catch (error) {
      console.error("Error updating order size:", error.message);
      NotificationManager.error('Failed to update order size');
    }
  };

  // Update balance limits
  const updateLimit = async (min_usdt_balance, min_limo_balance) => {
    try {
      const response = await fetch(`${BaseUrl}/update_balances`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bot_id: 2,
          min_usdt_balance,
          min_limo_balance,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update balances");
      }

      fetchLimits(); // Refresh values after update
      NotificationManager.success('Balance limits updated successfully!');
    } catch (error) {
      console.error("Error updating balances:", error.message);
      NotificationManager.error('Failed to update balance limits');
    }
  };

  // Handle modal show for different types of inputs
  const handleShow = (title, currentValue, type = 'single') => {
    setModalTitle(title);
    setInputType(type);
    
    if (type === 'range') {
      const [min, max] = currentValue.split('-').map(val => val.trim());
      setInputValue(min);
      setSecondInputValue(max);
    } else {
      setInputValue(currentValue);
      setSecondInputValue('');
    }
    
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setInputValue('');
    setSecondInputValue('');
  };

  const handleSave = () => {
    if (modalTitle.includes('Trading Frequency')) {
      const min = parseInt(inputValue);
      const max = parseInt(secondInputValue);
      if (!isNaN(min) && !isNaN(max) && min > 0 && max > min) {
        updateTradingInterval(min, max);
      } else {
        NotificationManager.error('Invalid interval values');
      }
    } else if (modalTitle.includes('Order Size')) {
      const min = parseInt(inputValue);
      const max = parseInt(secondInputValue);
      if (!isNaN(min) && !isNaN(max) && min > 0 && max > min) {
        updateOrderSize(min, max);
      } else {
        NotificationManager.error('Invalid order size values');
      }
    } else {
      const value = parseFloat(inputValue);
      if (isNaN(value)) return;

      const field = modalTitle.toLowerCase().includes("usdt")
        ? "min_usdt_balance"
        : "min_limo_balance";

      const updatedUsdt = field === "min_usdt_balance" ? value : minUsdtBalance;
      const updatedLimo = field === "min_limo_balance" ? value : minLimoBalance;

      updateLimit(updatedUsdt, updatedLimo);
    }
    handleClose();
  };

  // Fetch LIMO/USDT price & volume
  const fetchLimoPriceVolume = async () => {
    try {
      const response = await fetch("https://openapi.digifinex.com/v3/ticker?symbol=limo_usdt");
      const data = await response.json();
  
      if (data.ticker && data.ticker.length > 0) {
        const ticker = data.ticker[0]; 
        setLimoPrice(parseFloat(ticker.last).toFixed(6));
        setLimoVolume(parseFloat(ticker.base_vol).toFixed(2));
      } else {
        console.error("Error fetching DigiFinex data:", data);
      }
    } catch (error) {
      console.error("Failed to fetch LIMO/USDT data from DigiFinex:", error);
    }
  };

  // Fetch daily balances
  useEffect(() => {
    const fetchDailyBalances = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BaseUrl}/daily_balances?bot_id=2`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const res = await response.json();
        
        if (!Array.isArray(res)) {
          throw new Error("Expected an array of data");
        }
  
        const cleanedData = res.map((item) => ({
          ...item,
          limo_balance: parseFloat(item.limo_balance),
          usdt_balance: parseFloat(item.usdt_balance),
          volume: parseFloat(item.volume),
        }));
  
        const sortedData = cleanedData.sort(
          (a, b) => new Date(b.recorded_at) - new Date(a.recorded_at)
        );
  
        const dataWithDiff = sortedData.map((item, index, arr) => {
          if (index === arr.length - 1) {
            return { ...item, usdt_diff: 0, limo_diff: 0 };
          }
  
          const next = arr[index + 1];
          return {
            ...item,
            usdt_diff: item.usdt_balance - next.usdt_balance,
            limo_diff: item.limo_balance - next.limo_balance,
          };
        });
  
        setBalances(dataWithDiff);
      } catch (error) {
        console.error("Error fetching balances:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLimits();
    fetchLimoPriceVolume();
    fetchDailyBalances();
    
    const priceInterval = setInterval(fetchLimoPriceVolume, 30000);
    return () => clearInterval(priceInterval);
  }, []);

  // Calculate Weekly & Monthly Average Difference
  const calculateAverages = () => {
    const now = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(now.getDate() - 7);
    const lastMonth = new Date();
    lastMonth.setDate(now.getDate() - 30);

    const weeklyData = balances.filter((b) => new Date(b.recorded_at) >= lastWeek);
    const monthlyData = balances.filter((b) => new Date(b.recorded_at) >= lastMonth);

    const avgWeeklyUSDT =
      weeklyData.reduce((sum, b) => sum + b.usdt_diff, 0) / (weeklyData.length || 1);
    const avgWeeklyLIMO =
      weeklyData.reduce((sum, b) => sum + b.limo_diff, 0) / (weeklyData.length || 1);

    const avgMonthlyUSDT =
      monthlyData.reduce((sum, b) => sum + b.usdt_diff, 0) / (monthlyData.length || 1);
    const avgMonthlyLIMO =
      monthlyData.reduce((sum, b) => sum + b.limo_diff, 0) / (monthlyData.length || 1);

    return {
      avgWeeklyUSDT: avgWeeklyUSDT.toFixed(2),
      avgWeeklyLIMO: avgWeeklyLIMO.toFixed(2),
      avgMonthlyUSDT: avgMonthlyUSDT.toFixed(2),
      avgMonthlyLIMO: avgMonthlyLIMO.toFixed(2),
    };
  };

  const averages = calculateAverages();

  // Initialize DataTable
  useEffect(() => {
    if (balances.length > 0) {
      const table = $("#dataTable").DataTable({
        destroy: true,
        pageLength: 10,
      });
      return () => {
        table.destroy();
      };
    }
  }, [balances]);

  // Check authentication
  useEffect(() => {
    const token = sessionStorage.getItem("AuthToken");
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Handle bot restart
  const handleRestart = async () => {
    try {
      const response = await axios.get('https://df-vibe-bot.pricebubble.in');
      NotificationManager.success('Restarted Successfully!');
    } catch (error) {
      console.error('Error triggering restart:', error);
      NotificationManager.warning('Failed to trigger restart.');
    }
  };

  return (
    <div>
      <Row className="g-4">
        <Col xs={12}>
          <Button 
            variant="warning" 
            className="mb-2" 
            onClick={handleRestart}
            style={{ borderRadius: '8px' }}
          >
            🔄 Restart
          </Button>
        </Col>
        
        {/* Price and Volume Card */}
        <Col xl={6} lg={6}>
          <Card className="widget-flat shadow-lg border-0 bg-dark text-white position-relative overflow-hidden"
            style={{ height: "220px", borderRadius: "15px", background: "linear-gradient(135deg, #1f1c2c, #928DAB)" }}>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center position-relative">
              <div className="text-center animate__animated animate__fadeIn">
                <img src="/assets/images/digi.png" alt="Digifinex Logo"
                  className="img-fluid mb-2 p-2 bg-light rounded-circle"
                  style={{ maxWidth: "60px" }} />
                {limoPrice && limoVolume ? (
                  <>
                    <h3 className="fw-bold text-warning">Price: ${limoPrice}</h3>
                    <h4 className="text-info">Volume (24H): {limoVolume}</h4>
                  </>
                ) : (
                  <p className="text-muted">Loading price data...</p>
                )}
              </div>
              <div className="position-absolute top-0 start-0 w-100 h-100" 
                style={{ background: "radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 80%)" }} />
            </Card.Body>
          </Card>
        </Col>

        {/* Average Differences Card */}
        <Col xl={6} lg={6}>
          <Card className="widget-flat shadow-lg border-0 bg-dark text-white position-relative overflow-hidden"
            style={{ height: "220px", borderRadius: "15px", background: "linear-gradient(135deg, #1f1c2c, #928DAB)" }}>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center position-relative">
              <div className="text-center animate__animated animate__fadeIn">
                <img src="/assets/images/digi.png" alt="Digifinex Logo"
                  className="img-fluid mb-2 p-2 bg-light rounded-circle"
                  style={{ maxWidth: "60px" }} />
                <div className="d-flex align-items-center gap-3 mt-2">
                  <h4 className="text-warning mb-0">Weekly (Difference):</h4>
                  <span className="text-info fw-bold fw-bold ">USDT: {averages.avgWeeklyUSDT}</span> 
                  <span className="text-info fw-bold fw-bold ">LIMO: {averages.avgWeeklyLIMO}</span> 
                </div>

                <div className="d-flex align-items-center gap-3 mt-2">
                  <h4 className="text-warning mb-0">Monthly (Difference):</h4>
                  <span className="text-info fw-bold">USDT: {averages.avgMonthlyUSDT}</span>
                  <span className="text-info fw-bold">LIMO: {averages.avgMonthlyLIMO}</span> 
                </div>
              </div>
              <div className="position-absolute top-0 start-0 w-100 h-100" 
                style={{ background: "radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 80%)" }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <br></br>
      
      <Row className="g-4">
        {/* Balance Limits Card */}
        <Col xl={6} lg={6}>
          <Card className="widget-flat shadow-lg border-0 bg-dark text-white position-relative overflow-hidden"
            style={{ height: "220px", borderRadius: "15px", background: "linear-gradient(135deg, #1f1c2c, #928DAB)" }}>
            <Card.Body className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 2 }}>
              <div className="text-left animate__animated animate__fadeIn">
                <>
                  {/* USDT Limit with Update */}
                  <div className="d-flex align-items-center justify-content-between bg-secondary p-2 rounded mb-2">
                    <h6 className="text-info mb-0">USDT LIMIT: ${minUsdtBalance}</h6>
                    <Button
                      size="sm"
                      variant="light"
                      onClick={() => handleShow('USDT LIMIT', minUsdtBalance)}
                    >
                      Update
                    </Button>
                  </div>
                  <br></br>
                  
                  {/* LIMO Limit with Update */}
                  <div className="d-flex align-items-center justify-content-between bg-secondary p-2 rounded">
                    <h6 className="text-info mb-0">LIMO LIMIT: {minLimoBalance}</h6>
                    <Button
                      size="sm"
                      variant="light"
                      onClick={() => handleShow('LIMO LIMIT', minLimoBalance)}
                    >
                      Update
                    </Button>
                  </div>
                </>
              </div>

              <div className="position-absolute top-0 start-0 w-100 h-100"
                style={{ background: "radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 80%)", pointerEvents: 'none' }} />
            </Card.Body>
          </Card>
        </Col>
        
        {/* Trading Settings Card */}
        <Col xl={6} lg={6}>
          <Card className="widget-flat shadow-lg border-0 bg-dark text-white position-relative overflow-hidden"
            style={{ height: "220px", borderRadius: "15px", background: "linear-gradient(135deg, #1f1c2c, #928DAB)" }}>
            <Card.Body className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 2 }}>   
              <div className="text-left animate__animated animate__fadeIn">
                <>
                  {/* Trading Frequency with Update */}
                  <div className="d-flex align-items-center justify-content-between bg-secondary p-2 rounded mb-2">
                    <h6 className="fw-bold text-info mb-0">Trading Frequency: {minIntervalSec} - {maxIntervalSec} Sec</h6>
                    <Button
                      size="sm"
                      variant="light"
                      onClick={() => handleShow('Trading Frequency', `${minIntervalSec} - ${maxIntervalSec}`, 'range')}
                    >
                      Update
                    </Button>
                  </div>
                  <br></br>

                  {/* Order Size with Update */}
                  <div className="d-flex align-items-center justify-content-between bg-secondary p-2 rounded">
                    <h6 className="text-info mb-0">Order Size: {minOrderSize} - {maxOrderSize} (LIMO)</h6>
                    <Button
                      size="sm"
                      variant="light"
                      onClick={() => handleShow('Order Size', `${minOrderSize} - ${maxOrderSize}`, 'range')}
                    >
                      Update
                    </Button>
                  </div>
                </>
              </div>
              <div className="position-absolute top-0 start-0 w-100 h-100"
                style={{ background: "radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 80%)", pointerEvents: 'none' }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Balance Data Table */}
      <div className="card basic-data-table">
        <div className="card-body">
          <h5 className="card-title">Balance Data</h5>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="table-responsive">
              <table className="table bordered-table mb-0" id="dataTable" data-page-length={10}>
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>Limo Balance</th>
                    <th>Difference (Limo)</th>
                    <th>USDT Balance</th>
                    <th>Difference (USDT)</th>
                    <th>Volume (24H)</th>
                    <th>Exchange</th>
                    <th>Recorded At</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((balance, index) => (
                    <tr key={balance.id}>
                      <td>{index + 1}</td>
                      <td>{balance.limo_balance.toFixed(2)}</td>
                      <td className={balance.limo_diff >= 0 ? "text-success" : "text-danger"}>
                        {balance.limo_diff.toFixed(2)}
                      </td>
                      <td>{balance.usdt_balance.toFixed(2)}</td>
                      <td className={balance.usdt_diff >= 0 ? "text-success" : "text-danger"}>
                        {balance.usdt_diff.toFixed(2)}
                      </td>
                      <td>{balance.volume.toFixed(2)}</td>
                      <td>{balance.exchange}</td>
                      <td>{new Date(balance.recorded_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Update Modal */}
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update {modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{inputType === 'range' ? 'Enter minimum value' : 'Enter new value'}</Form.Label>
            <Form.Control
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={inputType === 'range' ? 'Minimum value' : 'New value'}
            />
            
            {inputType === 'range' && (
              <>
                <Form.Label className="mt-3">Enter maximum value</Form.Label>
                <Form.Control
                  type="number"
                  value={secondInputValue}
                  onChange={(e) => setSecondInputValue(e.target.value)}
                  placeholder="Maximum value"
                />
              </>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Balances;